import type { MessageRouter, ReceiverInput } from "@pdfking/functionpool"
import { CachedForward } from "@pdfking/functionpool"
import { type CacheBackend, FileCache } from "@pdfking/storage"
import type { StringGUID } from "@pdfking/tfunc"
import {
  All,
  ArrayToIterable,
  CreateSingleton,
  First,
  SafeIterable,
} from "@pdfking/tfunc"

import { AutoThrow } from "../errors/AutoThrow"
import { FileContentGUID } from "../methods/FileContentGUID"
import type { APIRequest } from "../types/api/APIRequest"
import type { APIResponse } from "../types/api/APIResponse"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import { TaskName } from "../types/tasks/TaskName"
import type { TaskOptions } from "../types/tasks/TaskOptions"
import type { TrackingInterface } from "../types/tasks/tracking/TrackingInterface"
import { CreateEngineVirtual } from "./CreateEngineVirtual"
import { CreatePdfEngineDefaults } from "./CreatePdfEngineDefaults"

function emit_callbacks(
  forward: MessageRouter<APIRequest, APIResponse>,
  tracking_callback: TrackingInterface | undefined = undefined
): MessageRouter<APIRequest, APIResponse> {
  return async function* (original) {
    const [task_id, request] = await extract_task_id(original)

    tracking_callback?.start(task_id)

    const { stream, error } = SafeIterable(forward(request))

    for await (const response of stream) {
      if (response.enum == TaskName.TRACKING) {
        tracking_callback?.push(task_id, response)
      } else {
        yield response
      }
    }

    tracking_callback?.stop(task_id, await error)

    await AutoThrow(error)
  }
}

function invalidateCache(
  config: PdfEngineOptions,
  cache: CacheBackend<APIResponse[]>
): CacheBackend<APIResponse[]> {
  return {
    ...cache,
    get: async key => {
      const response = await cache.get(key)

      if (response) {
        const result = await Promise.all(
          response.map(r =>
            r.enum != TaskName.TRACKING
              ? FileCache(config.file_cache).exists(FileContentGUID(r.content))
              : true
          )
        )
        if (All(result)) return response
        await cache.delete(key)

        return undefined
      }

      return response
    },
  }
}

async function extract_task_id(
  stream: ReceiverInput<APIRequest>
): Promise<[guid: StringGUID, stream: ReceiverInput<APIRequest>]> {
  const options = (await First(stream)) as TaskOptions
  return [options.task_id, ArrayToIterable([options], stream)]
}

export const CreateEngineMaster = (
  config: Partial<PdfEngineOptions>,
  factory: (
    router: MessageRouter<APIRequest, APIResponse>
  ) => MessageRouter<APIRequest, APIResponse>,
  cache: CacheBackend<APIResponse[]> | undefined = undefined,
  tracking_callback: TrackingInterface | undefined = undefined
): PdfEngine => {
  const router = CreateSingleton(() => factory(forward))

  const forward = CachedForward(
    emit_callbacks(t => router()(t), tracking_callback),
    extract_task_id,
    cache ? invalidateCache(CreatePdfEngineDefaults(config), cache) : undefined
  )

  return CreateEngineVirtual(config, forward)
}
